import React, { useCallback } from 'react';
import {
  type LayoutChangeEvent,
  StyleSheet,
  View,
  type ViewProps,
} from 'react-native';
import Animated, {
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { theme } from '@garnish/constants';

import { ANIMATED_DIALOG_ANIMATION_CONFIG } from './AnimatedDialog.constants';
import { AnimatedDialogOverlay } from './AnimatedDialogOverlay';

// ─────────────────────────────────────────────────────────────────────────────
// This is a react-native-reanimated view that behaves like a modal.
// ─────────────────────────────────────────────────────────────────────────────

export const AnimatedDialog = (props: AnimatedDialogProps) => {
  const {
    children,
    show,
    maxHeight,
    style,
    dismissOverlayAccessibilityLabel = 'Dismiss modal',
    onLayout,
    dismissModal,
  } = props;

  // ─── Height Animation ─────────────────────────────────────────────────────

  const height = useSharedValue(0);

  const heightAnimation = useAnimatedStyle(() => {
    return {
      height: maxHeight?.value
        ? Math.min(height.value, maxHeight.value)
        : height.value,
    };
  });

  const syncHeight = useCallback(
    (event: LayoutChangeEvent) => {
      const layoutHeight = event.nativeEvent.layout.height;

      height.value = withTiming(layoutHeight, ANIMATED_DIALOG_ANIMATION_CONFIG);
      onLayout?.(event);
    },
    [height, onLayout],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.modalContainer} pointerEvents={show ? 'auto' : 'none'}>
      <AnimatedDialogOverlay
        show={show}
        accessibilityLabel={dismissOverlayAccessibilityLabel}
        dismissModal={dismissModal}
      />

      <Animated.View
        pointerEvents="none"
        style={[styles.modalBackground, heightAnimation]}
      />

      <View onLayout={syncHeight} style={[styles.dialogContent, style]}>
        {children}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  modalContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    justifyContent: 'flex-end',
    zIndex: theme.zIndex.modal,
  },
  modalBackground: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: 0,
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
    backgroundColor: theme.colors.CREAM,
    zIndex: theme.zIndex.modalBackdrop,
    ...theme.elevations[2],
  },
  dialogContent: {
    flexShrink: 1,
    marginTop: 120,
    zIndex: theme.zIndex.modal,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type AnimatedDialogProps = {
  children: React.ReactNode;
  show: boolean;
  style?: ViewProps['style'];
  maxHeight?: SharedValue<number>;
  dismissOverlayAccessibilityLabel?: string;
  onLayout?: ViewProps['onLayout'];
  dismissModal: () => void;
};
